<template>
    <div class="border-gray-100 border overflow-hidden mb-4 contacts-card">
        <div class="px-3 py-2 border-bottom border-gray-100 d-flex align-items-center justify-content-between gap-12">
            <div class="d-flex align-items-center gap-2">
                <span class="text-ashore-black fs-14 font-weight-bold">Contacts</span>
                <span v-if="contacts.length > 0" class="fs-14 text-gray-500">{{ contacts.length }}</span>
            </div>
            <button
                v-if="contacts.length > 0"
                class="attach-btn btn d-flex align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                @click="openAttachModal">
                <img src="/img/plus-light.svg" width="12" alt="" />
                Attach Contact
            </button>
        </div>

        <div class="contacts-body">
            <button
                v-if="contacts.length === 0"
                type="button"
                class="contacts-empty"
                @click="openAttachModal">
                <span class="contacts-empty__glyph" aria-hidden="true">
                    <img src="/img/users.svg" width="20" alt="" />
                </span>
                <span class="contacts-empty__title">No contacts attached to this job</span>
                <span class="contacts-empty__hint">
                    <img src="/img/plus-light.svg" width="12" alt="" />
                    Attach a contact
                </span>
            </button>

            <ul v-else class="contacts-list list-unstyled mb-0">
                <li
                    v-for="contact in contacts"
                    :key="contact.id"
                    class="contact-row d-flex align-items-center gap-12 px-3 py-12">
                    <span class="contact-avatar flex-shrink-0" :style="{ background: avatarBg(contact.id) }">
                        {{ contact.initials || initialsFor(contact.name) }}
                    </span>
                    <div class="contact-meta flex-fill min-w-0">
                        <div class="contact-name text-truncate text-ashore-black fs-14" :title="contact.name">
                            {{ contact.name }}
                        </div>
                        <div class="contact-email text-truncate fs-12 text-gray-500 mt-1" :title="contact.email">
                            {{ contact.email }}
                        </div>
                    </div>

                    <div class="dropdown dropdown-wrapper-js" draggable="false">
                        <button class="btn p-1 dropdown-toggle-js" @click.stop="toggleMenu(contact)">
                            <img src="/img/dots-horizontal.svg" width="14" alt="" />
                        </button>
                        <div
                            class="dropdown-menu dropdown-js rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden"
                            :class="{ show: openMenuId === contact.id }"
                            style="min-width: 180px;"
                            @click.stop>
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush">
                                <li class="p-0 bg-white">
                                    <span
                                        class="dropdown-item text-danger d-flex align-items-center gap-12 py-2 px-3"
                                        @click="confirmRemove(contact)">
                                        Remove
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <modal-attach-contacts
            :modal-id="attachModalId"
            :attached-contacts="contacts"
            @save="handleAttachSave" />

        <b-modal :id="confirmModalId" centered hide-footer hide-header>
            <div class="py-2">
                <h5 class="modal-heading mb-2">Remove contact?</h5>
                <p class="text-muted fs-14 mb-0">
                    Remove
                    <span class="fw-medium text-ashore-black">{{ contactToRemove ? contactToRemove.name : '' }}</span>
                    from this job?
                </p>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-4">
                <button class="btn cancel-btn fw-normal" @click="cancelRemove">Cancel</button>
                <button class="btn btn-danger fw-normal" @click="performRemove">Remove</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import modalAttachContacts from "../modals/modal-attach-contacts.vue";

const PALETTE = [
    "#019256", "#4F46E5", "#DC2626", "#D97706", "#0891B2",
    "#7C3AED", "#DB2777", "#059669", "#2563EB", "#EA580C"
];

export default {
    name: "AssociatedContacts",
    components: { modalAttachContacts },
    props: {
        jobId: { type: Number, default: null },
        contacts: { type: Array, default: () => [] },
    },
    data() {
        const suffix = this.jobId != null ? this.jobId : `stage-${Math.random().toString(36).slice(2, 8)}`;
        return {
            openMenuId: null,
            contactToRemove: null,
            confirmModalId: `removeContactModal-${suffix}`,
            attachModalId: `modalAttachContacts-${suffix}`,
        };
    },
    computed: {
        isStaging() {
            return this.jobId == null;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideClick);
    },
    methods: {
        openAttachModal() {
            this.$bvModal.show(this.attachModalId);
        },
        handleAttachSave({ added, removed, selectedContacts }) {
            if (this.isStaging) {
                this.$emit("update:contacts", selectedContacts);
                return;
            }
            this.applyServerUpdate({ added, removed });
        },
        async applyServerUpdate({ added, removed }) {
            try {
                await this.$A.JobService.updateContactLink({
                    jobId: this.jobId,
                    update: added,
                    delete: removed,
                });
                this.$emit("contacts-changed");
            } catch (e) {
                console.error("Failed to update contacts:", e);
            }
        },
        toggleMenu(contact) {
            this.openMenuId = this.openMenuId === contact.id ? null : contact.id;
        },
        handleOutsideClick() {
            this.openMenuId = null;
        },
        initialsFor(name) {
            if (!name) return "?";
            return name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0])
                .join("")
                .toUpperCase();
        },
        avatarBg(id) {
            const n = Number(id) || 0;
            return PALETTE[n % PALETTE.length];
        },
        confirmRemove(contact) {
            this.openMenuId = null;
            this.contactToRemove = contact;
            this.$bvModal.show(this.confirmModalId);
        },
        cancelRemove() {
            this.$bvModal.hide(this.confirmModalId);
            this.contactToRemove = null;
        },
        async performRemove() {
            const contact = this.contactToRemove;
            this.$bvModal.hide(this.confirmModalId);
            this.contactToRemove = null;
            if (!contact) return;

            if (this.isStaging) {
                const next = this.contacts.filter((c) => c.id !== contact.id);
                this.$emit("update:contacts", next);
                return;
            }

            await this.applyServerUpdate({ added: [], removed: [contact.id] });
        },
    },
};
</script>

<style scoped>
.contacts-card {
    border-radius: 6px;
}
.contacts-card:has(.dropdown-menu.show) {
    overflow: visible !important;
}

.attach-btn {
    border: 1px solid #E1E3E8;
    background: #FFFFFF;
    color: #4B5563;
}
.attach-btn:hover {
    border-color: #019256;
    color: #019256;
}

.contacts-body { position: relative; }

.contacts-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: calc(100% - 24px);
    margin: 12px;
    padding: 28px 16px;
    border: 1.5px dashed #E1E3E8;
    border-radius: 10px;
    background:
        radial-gradient(120% 80% at 50% 0%, rgba(79, 70, 229, 0.05), transparent 70%),
        #FCFCFD;
    color: #6B7280;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
    appearance: none;
    outline: none;
}
.contacts-empty:hover {
    border-color: #4F46E5;
    background:
        radial-gradient(120% 80% at 50% 0%, rgba(79, 70, 229, 0.08), transparent 65%),
        #FFFFFF;
    transform: translateY(-1px);
}
.contacts-empty__glyph {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(79, 70, 229, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.04);
}
.contacts-empty__glyph img { opacity: 0.7; }
.contacts-empty__title {
    font-size: 13px;
    font-weight: 500;
    color: #4B5563;
}
.contacts-empty__hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #9CA3AF;
}
.contacts-empty__hint img { opacity: 0.55; }

.contacts-list { padding: 0; }
.contact-row {
    transition: background 0.12s ease;
    position: relative;
}
.contact-row + .contact-row {
    border-top: 1px solid #F4F4F6;
}
.contact-row:hover { background: #F9FAFB; }
.contact-row:has(.dropdown-menu.show) { z-index: 1100; }

.contact-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: -0.2px;
}

.modal-heading {
    color: #3A3A3A;
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
}
.cancel-btn { background-color: #EEF2F5 !important; }

.dropdown-menu {
    z-index: 1050 !important;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
}
.dropdown-menu.show {
    opacity: 1;
    visibility: visible;
}
</style>
